import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import buildTheme from './theme/dataflix';
import { brand } from './brand';

const ThemeModeContext = createContext(null);

const STORAGE_KEY = 'smarthire.themeMode';
// The last theme this browser was served. Kept so a repeat visit paints in the
// right colours immediately instead of flashing blue and then correcting
// itself; it is refreshed from the server on every load.
const APPEARANCE_KEY = 'smarthire.appearance';

const readStoredAppearance = () => {
  try {
    const raw = window.localStorage.getItem(APPEARANCE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && parsed.tokens ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Load the guide's typefaces.
 *
 * Each theme names its own families and they are not all in index.html, so the
 * stylesheet is added when a theme asks for a family that is not already there.
 * Links are never removed: a font that was in use a moment ago may still be
 * painting, and the cost of leaving one behind is a cached request.
 */
const loadFonts = (appearance) => {
  const families = [appearance?.tokens?.fonts?.sans, appearance?.tokens?.fonts?.mono, appearance?.tokens?.fonts?.serif]
    .filter(Boolean)
    // Anything from the guide goes into a URL, so only plain family names pass.
    .filter((family) => /^[\w][\w '-]{0,40}$/.test(family));
  for (const family of families) {
    const id = `font-${family.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    if (document.getElementById(id)) continue;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}:wght@400;500;600;700&display=swap`;
    document.head.appendChild(link);
  }
};

/**
 * What the operating system is asking for.
 *
 * Used only as the starting point. Once someone picks a mode themselves that
 * choice wins, because an explicit preference outranks an inferred one.
 */
const systemPrefers = () => {
  try {
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  } catch {
    // matchMedia is missing in some test environments. The guide is dark-first.
    return 'dark';
  }
};

const readStoredMode = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : null;
  } catch {
    return null;
  }
};

export const ThemeModeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => readStoredMode() || systemPrefers());
  const [chosen, setChosen] = useState(() => readStoredMode() !== null);
  const [appearance, setAppearance] = useState(readStoredAppearance);

  /**
   * What the operator has chosen for everybody.
   *
   * Unauthenticated, because the login and careers pages are painted before
   * anyone has a session. A failure is survivable in both directions: the
   * cached theme keeps working, and with no cache the built-in palette does.
   */
  const refreshAppearance = useCallback(async ({ force = false } = {}) => {
    try {
      // The response is cacheable for a minute so it costs other people
      // nothing, which means the operator who just changed it would see their
      // own stale copy. A forced read steps around that cache.
      const { data } = await axios.get(
        force ? `/api/public/appearance?at=${Date.now()}` : '/api/public/appearance',
        force ? { headers: { 'Cache-Control': 'no-cache' } } : undefined
      );
      if (!data?.tokens) return null;
      setAppearance(data);
      loadFonts(data);
      try {
        window.localStorage.setItem(APPEARANCE_KEY, JSON.stringify(data));
      } catch {
        // A remembered theme is a convenience; losing it costs one flash.
      }
      return data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (appearance) loadFonts(appearance);
    refreshAppearance();
    // A theme applied by an operator should reach other people's browsers
    // without them reloading, so it is re-read whenever a tab comes forward.
    const onFocus = () => refreshAppearance();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshAppearance]);

  // Follow the system until the user overrides it, then stop listening to it.
  useEffect(() => {
    if (chosen) return undefined;
    let media;
    try {
      media = window.matchMedia('(prefers-color-scheme: light)');
    } catch {
      return undefined;
    }
    const onChange = (event) => setMode(event.matches ? 'light' : 'dark');
    media.addEventListener?.('change', onChange);
    return () => media.removeEventListener?.('change', onChange);
  }, [chosen]);

  const setThemeMode = useCallback((next) => {
    setMode(next);
    setChosen(true);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // A remembered preference is a convenience; losing it is survivable.
    }
  }, []);

  const toggleThemeMode = useCallback(
    () => setThemeMode(mode === 'dark' ? 'light' : 'dark'),
    [mode, setThemeMode]
  );

  // A guide with no dark palette of its own is shown in light. Inverting one
  // is not the same as designing one, and it looks it.
  const supportsDark = appearance ? appearance.supportsDark !== false : true;
  const effectiveMode = supportsDark ? mode : 'light';

  const theme = useMemo(
    () => createTheme(buildTheme(effectiveMode, appearance, brand.color)),
    [effectiveMode, appearance]
  );

  // The browser chrome and any native control should match the page.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', effectiveMode);
    document.documentElement.style.colorScheme = effectiveMode;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme.palette.background.default);
  }, [effectiveMode, theme]);

  const value = useMemo(
    () => ({
      mode: effectiveMode,
      setThemeMode,
      toggleThemeMode,
      supportsDark,
      appearance,
      refreshAppearance
    }),
    [effectiveMode, setThemeMode, toggleThemeMode, supportsDark, appearance, refreshAppearance]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used inside a ThemeModeProvider');
  }
  return context;
};

export default ThemeModeContext;
